import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Layout, Header } from 'app/theme/layout'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import CateringOccasionsSlider from 'app/theme/sections/CateringOccasionsSlider'
import Calculator from 'app/theme/calculator'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from './shared/OccasionPageGrid'
import { useInView } from 'react-intersection-observer'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import hero from 'assets/img/food-truck-corporate-background.jpg'
import corporateCatering1 from 'assets/img/corporate-catering-1.jpg'
import corporateCatering2 from 'assets/img/corporate-catering-2.jpg'
import corporateCatering3 from 'assets/img/corporate-catering-3.jpg'
import corporateCatering4 from 'assets/img/corporate-catering-4.jpg'

export default function CorporateCateringPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Corporate Catering with Delicious Food Trucks - Roaming Hunger</title>
        <meta name="description" content="Book food trucks for your next office lunch catering, employee appreciation day, corporate event, or business meeting."></meta>
        <meta name="og:title" content="Corporate Catering with Delicious Food Trucks"></meta>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={hero}
          onButtonClick={openModal}
          title="Workplace Catering"
          description={(
            <span>
              Food trucks are an easy and inexpensive way to celebrate a company
              milestone or retain talent through an employee perk program.
              Engage employees, increase productivity and show everyone on the
              team how valued they are.
            </span>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}
      <Intro />
      <IntroGrid />
      <FeaturedClients onButtonClick={openModal} />
      <WhyWorkWithUsGrid />
      <Calculator padding="py-10" onButtonClick={openModal} />
      <WhyWorkWithUs padding="pt-10 pb-16" onButtonClick={openModal} />
      <HowItWorks heading="How Workplace Catering Works" onButtonClick={openModal} />
      <HowItWorksLarge heading="How Workplace Catering Works" onButtonClick={openModal} />
      <CateringOccasionsSlider
        heading="Other catering occasions"
        className="bg-white py-10"
      />
    </Layout>
  )
}

function Intro() {
  return (
    <div className="container text-center pt-12">
      <h2 className="text-navy text-4xl leading-tight font-platform">
        Workplace Catering with Delicious Food Trucks
      </h2>
      <p className="font-light text-gray-900 mt-6">
        Bring food trucks to your next employee or client appreciation day, team
        building exercise or simply to reward your staff with gourmet food.
        Including a catering option in your employee incentive program is a
        great way to motivate your staff.
      </p>
    </div>
  )
}


function IntroGrid() {
  return (
    <OccasionPageGrid className="py-10">
      <OccasionPageGridRow
        heading="Your Company Is Covered"
        content="Get back to doing more important things. Experience our seamless booking with a single point of contact and your corporate catering preferences on file. We handle all the logistics, whether you plan to book food trucks once or over and over again."
        image={corporateCatering1}
        reverse
      />
      <OccasionPageGridRow
        heading="Something for Everyone"
        content="Keep your entire office well fed. With a network of 15,500 food trucks, we secure a variety of food options no matter where you work. From lobster rolls, to tacos or gourmet salads, we ensure a delicious mix designed to make everyone happy."
        image={corporateCatering2}
      />
    </OccasionPageGrid>
  )
}

function WhyWorkWithUsGrid() {
  return (
    <OccasionPageGrid className="py-10">
      <OccasionPageGridRow
        heading="Celebrate Your Hardworking Staff"
        content="Give the whole team a reason to bond. We bring food trucks to your next employee appreciation day and team building exercise, or schedule regular lunch services to reward everyone in the company. You'll be a food truck hero!"
        image={corporateCatering3}
        reverse
      />
      <OccasionPageGridRow
        heading="Book With Confidence"
        content="Book your corporate catering event with confidence knowing we have established relationships with thousands of food trucks. We hold all funds until after your event, so we can guarantee you'll get fantastic customer service every time."
        image={corporateCatering4}
      />
    </OccasionPageGrid>
  )
}
